import { CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import clsx from 'clsx';
import parse, { domToReact, Element } from 'html-react-parser';
import { createElement, ElementType, useCallback, useRef } from 'react';

import LexicalEditorContent from '~components/generic/editable-field/components/LexicalEditorContent';
import { CustomParagraphNode } from '~components/generic/editable-field/components/nodes/CustomParagraphNode';
import { DEFAULT_TOOLBAR_CONTROLS } from '~components/generic/editable-field/constants/toolbar';
import { Link } from '~components/generic/elements/Link/Link';
import { getEntityFieldDefinition } from '~services/entity-definitions';
import Theme from '../Theme';
import styles from './EditableField.css';

type ClassNameShape = string | { [key: string]: boolean };

interface EditorProps {
  editing: boolean;
  fieldName: string;
  onChange?: (field: string, value: string) => void;
  data?: string;
  entityType?: string;
  hide?: boolean;
  className?: ClassNameShape;
  disableDrop?: boolean;
  index?: number | string;
  toolbarControls?: string[];
}

const editorConfig = {
  namespace: 'Lexical Editor',
  // Handling of errors during update
  onError(error: Error) {
    throw error;
  },
  nodes: [
    CustomParagraphNode,
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
  ],
  // The editor theme
  theme: Theme,
};

export const EditableField = (props: EditorProps) => {
  const { entityType, onChange, data, editing, fieldName, className, index, toolbarControls, hide } = props;
  const targetRef = useRef(null);
  const allControls = toolbarControls || DEFAULT_TOOLBAR_CONTROLS;
  const fieldDef = getEntityFieldDefinition(entityType, fieldName);
  const excludeControls = fieldDef?.options?.excludeControls || [];
  const includedControls = allControls?.filter(control => excludeControls.indexOf(control) === -1);

  // Check if the content already has HTML tags
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(data as string);
  // Check if is paragraph Title
  const isParagraphTitle = data?.includes('<p') && fieldName === 'attributes.field_title.value';

  // Fixed code for React 19.1.0 compatibility
  const tagName = hasHtmlTags && !isParagraphTitle ? 'div' : (fieldDef && fieldDef.tagName) || 'div';
  const DynamicTag = tagName as ElementType;

  const onChangeEditor = useCallback(
    (html: string) => {
      if (fieldDef?.multiple) {
        return onChange?.(`${fieldName}.${index}`, html);
      }

      onChange?.(fieldName, html);
    },
    [fieldDef?.multiple, fieldName, index, onChange],
  );

  const classes = clsx(
    {
      'lexical-editor': true,
      'c-module__field--editing': editing,
      [styles.base]: true,
      [styles.hide]: hide,
    },
    className,
  );

  if (editing) {
    return (
      <LexicalComposer initialConfig={editorConfig}>
        <LexicalEditorContent
          includedControls={includedControls}
          showAsHtmlCode={false}
          targetRef={targetRef}
          {...props}
          onChangeEditor={onChangeEditor}
        />
      </LexicalComposer>
    );
  }

  return createElement(
    DynamicTag,
    { ref: targetRef, className: classes },
    parse(data || '', {
      replace: domNode => {
        if (domNode instanceof Element) {
          const { type, name, attribs, children } = domNode;
          if (type !== 'tag' || name !== 'a') {
            return;
          }
          const { href } = attribs;
          if (href) {
            const [path, id] = href.indexOf('/#/') === -1 && href.indexOf('~') >= 1 ? href.split('#') : [href, ''];
            return createElement(Link, { id, url: !id && path }, domToReact(children as any));
          }
        }
        return;
      },
    }),
  );
};

export default EditableField;
